/**
 * =============================================================================
 * Componente: WorkspacePlaybooksTab
 * Versão: 1.0.0
 *
 * Descrição:
 *   Aba "Playbooks" exibida dentro do expand de um Workspace em
 *   /tenant/crm/workspaces. Permite:
 *     1. Ativar/desativar o modo Playbook (`playbook_mode_enabled`).
 *     2. Listar Playbooks de Atividades do workspace.
 *     3. Gerenciar regras de disparo automático (bindings) por
 *        pipeline/fase com triggers `deal_created` | `deal_enters_phase` |
 *        `manual`.
 *
 *   Padrão: leituras via RPC; mutations de bindings via RPC.
 *   O toggle `playbook_mode_enabled` ainda usa update direto na tabela
 *   `crm_workspaces` (mesmo padrão das demais flags do workspace).
 *
 * Release notes:
 *   v1.0.0 - Criação (Fase 1, item 1.14 do Playbook de Vendas).
 * =============================================================================
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ListChecks,
  Plus,
  Zap,
  PowerOff,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useActivityPlaybooks } from '@/hooks/useActivityPlaybooks';
import {
  useWorkspacePlaybookBindings,
} from '@/hooks/useWorkspacePlaybookBindings';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('WorkspacePlaybooksTab');

// ============================================================================
// Tipos auxiliares
// ============================================================================
interface WorkspacePhase {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
}

interface WorkspacePipeline {
  id: string;
  name: string;
  phases?: WorkspacePhase[];
}

interface Props {
  workspaceId: string;
  tenantId: string;
  playbookModeEnabled: boolean;
  pipelines: WorkspacePipeline[];
}

// ============================================================================
// Componente principal
// ============================================================================
export function WorkspacePlaybooksTab({
  workspaceId,
  tenantId,
  playbookModeEnabled,
  pipelines,
}: Props) {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canEdit, canCreate } = usePermissions();

  const canManagePlaybooks = canEdit('crm_playbooks') || canCreate('crm_playbooks');
  const canManageWorkspace = canEdit('crm_workspaces');

  // ----- Carrega playbooks e bindings deste workspace -----
  const { playbooks, isLoading: loadingPlaybooks } = useActivityPlaybooks(workspaceId);
  const {
    bindings,
    isLoading: loadingBindings,
    upsertBinding,
    isUpserting,
    deleteBinding,
  } = useWorkspacePlaybookBindings(workspaceId);

  // ----- Mutation: toggle playbook_mode_enabled -----
  // Update direto na tabela (mesmo padrão das demais flags do workspace),
  // RLS garante isolamento por tenant.
  const togglePlaybookModeMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from('crm_workspaces')
        .update({ playbook_mode_enabled: enabled })
        .eq('id', workspaceId)
        .eq('tenant_id', tenantId);
      if (error) throw error;
      return enabled;
    },
    onSuccess: (enabled) => {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      toast.success(
        enabled
          ? 'Modo Playbook ativado neste workspace'
          : 'Modo Playbook desativado neste workspace',
      );
    },
    onError: (err: any) => {
      logger.error('Erro ao alternar playbook_mode_enabled', err);
      toast.error(err?.message || 'Erro ao atualizar modo Playbook');
    },
  });

  // ----- Helpers de mapeamento (para exibir nomes amigáveis) -----
  const pipelineMap = useMemo(() => {
    const map = new Map<string, WorkspacePipeline>();
    pipelines.forEach((p) => map.set(p.id, p));
    return map;
  }, [pipelines]);

  // v1.2.0: contagem de gatilhos por playbook (bindings agora são geridos
  // dentro do próprio editor de playbook — aqui apenas exibimos o total).
  const triggersCountByPlaybook = useMemo(() => {
    const map = new Map<string, number>();
    bindings.forEach((b) => {
      map.set(b.playbook_id, (map.get(b.playbook_id) ?? 0) + 1);
    });
    return map;
  }, [bindings]);

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="space-y-4">
      {/* ===== Toggle Modo Playbook ===== */}
      <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2 text-primary">
            <Zap className="h-4 w-4" />
          </div>
          <div>
            <Label htmlFor={`pb-mode-${workspaceId}`} className="text-sm font-medium">
              Modo Playbook
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
              Quando ativo, novos negócios neste workspace recebem cadências
              automáticas de atividades conforme as regras configuradas abaixo.
            </p>
          </div>
        </div>
        <Switch
          id={`pb-mode-${workspaceId}`}
          checked={playbookModeEnabled}
          disabled={!canManageWorkspace || togglePlaybookModeMutation.isPending}
          onCheckedChange={(v) => togglePlaybookModeMutation.mutate(v)}
        />
      </div>

      {/* Aviso quando modo está desativado */}
      {!playbookModeEnabled && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400">
          <PowerOff className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            As regras abaixo só serão executadas quando o Modo Playbook
            estiver ativo neste workspace.
          </span>
        </div>
      )}

      {/* ===== Lista de Playbooks do workspace ===== */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            Playbooks deste Workspace
            {playbooks.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {playbooks.length}
              </Badge>
            )}
          </h4>
          <div className="flex items-center gap-1">
            {canManagePlaybooks && (
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={() =>
                  navigate(
                    `/tenant/crm/playbooks/new?workspace_id=${workspaceId}`,
                  )
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Novo Playbook
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => navigate('/tenant/crm/playbooks')}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Gerenciar
            </Button>
          </div>
        </div>

        {loadingPlaybooks ? (
          <p className="text-xs text-muted-foreground py-2">Carregando playbooks...</p>
        ) : playbooks.length === 0 ? (
          <div className="rounded-md border border-dashed p-3 text-center">
            <p className="text-xs text-muted-foreground">
              Nenhum playbook criado neste workspace.
            </p>
            {canManagePlaybooks && (
              <Button
                size="sm"
                variant="link"
                className="h-auto p-0 mt-1 text-xs"
                onClick={() => navigate('/tenant/crm/playbooks')}
              >
                Criar primeiro playbook →
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-1.5">
            {playbooks.map((pb) => (
              <div
                key={pb.id}
                className="flex items-center justify-between rounded-md border bg-background px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: pb.color || 'hsl(var(--primary))' }}
                  />
                  <span className="font-medium truncate">{pb.name}</span>
                  {!pb.is_active && (
                    <Badge variant="outline" className="text-[10px] h-4">
                      Inativo
                    </Badge>
                  )}
                  {/* v1.1.0: badge para playbook organizado por fase do pipeline */}
                  {pb.scope === 'per_phase' && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-4 gap-1"
                      title="Disparo automático por fase do pipeline"
                    >
                      Por fase
                      {pb.pipeline_id && pipelineMap.get(pb.pipeline_id)?.name
                        ? ` · ${pipelineMap.get(pb.pipeline_id)!.name}`
                        : ''}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                  <span title="Steps">{pb.steps_count} steps</span>
                  <span title="Cadências ativas">{pb.active_cadences_count} ativas</span>
                  {/* v1.2.0: contagem de gatilhos configurados no editor do playbook */}
                  {(triggersCountByPlaybook.get(pb.id) ?? 0) > 0 && (
                    <span title="Gatilhos configurados" className="flex items-center gap-0.5">
                      <Zap className="h-3 w-3" />
                      {triggersCountByPlaybook.get(pb.id)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== Aviso: gatilhos migrados para dentro do editor de playbook ===== */}
      <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-2.5 text-[11px] text-blue-700 dark:text-blue-400 flex items-start gap-2">
        <Zap className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          Os <strong>gatilhos de disparo automático</strong> agora são
          configurados dentro de cada playbook (aba "Gatilhos" no editor).
          Cada playbook pode ter uma ou mais regras (ao criar negócio, ao
          entrar em fase, disparo manual, etc.).
        </span>
      </div>
    </div>
  );
}
